import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Paper,
  Alert,
} from '@mui/material';
import {
  DeleteOutline as DeleteOutlineIcon,
  Close as CloseIcon,
  Search as SearchIcon,
  LockOutlined as LockOutlinedIcon,
} from '@mui/icons-material';
import { useProductosMaterialContext } from 'context';
import {
  tienePermisosModulo,
  puedeAccionFacturacion,
} from 'lib/seguridad/permisosPorModulo';
import { FP, PageHeader, fpInputSx } from 'components/financepro';

const PROGRAMA_PAGOS_DETALLE = 'pagosclientescreditodetalledialog';

const cardShellSx = {
  borderRadius: 2.5,
  border: `1px solid ${FP.color.border}`,
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
  overflow: 'hidden',
};

const fmtMoney = (v) =>
  (parseFloat(v) || 0).toLocaleString('es-DO', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const formatearFecha = (fecha) => {
  if (!fecha) return '—';
  try {
    const d = fecha.includes('T') ? new Date(fecha) : new Date(fecha + 'T12:00:00');
    return d.toLocaleDateString('es-DO', { year: 'numeric', month: '2-digit', day: '2-digit' });
  } catch {
    return fecha;
  }
};

const cellHeadSx = {
  fontWeight: 700,
  fontSize: '0.74rem',
  color: FP.color.textSecondary,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  bgcolor: FP.color.surfaceAlt,
  borderBottom: `1px solid ${FP.color.border}`,
  py: 1.2,
};

/**
 * Tabla reutilizable de cobros CXC (`pagoscli`). Recibe filas y callbacks
 * externos para conservarla 100% controlada por el padre.
 */
export function PagosClienteCreditoDetalleTable({
  rows = [],
  loading = false,
  onEliminar,
  eliminandoId = null,
  canDelete = false,
  emptyMessage = 'No hay pagos registrados para este cliente.',
  errorMessage = null,
}) {
  if (errorMessage) {
    return (
      <Alert
        severity="error"
        sx={{
          borderRadius: 2,
          border: `1px solid ${FP.color.border}`,
          bgcolor: FP.color.dangerSoft,
        }}
      >
        {errorMessage}
      </Alert>
    );
  }
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={32} />
      </Box>
    );
  }
  if (!rows.length) {
    return (
      <Typography variant="body2" sx={{ color: FP.color.textSecondary, fontSize: '0.9rem' }}>
        {emptyMessage}
      </Typography>
    );
  }
  return (
    <TableContainer
      component={Paper}
      elevation={0}
      sx={{
        border: `1px solid ${FP.color.border}`,
        borderRadius: 2,
        maxHeight: 420,
        bgcolor: FP.color.cardBg,
      }}
    >
      <Table size="small" stickyHeader>
        <TableBody>
          <TableRow
            sx={{
              position: 'sticky',
              top: 0,
              zIndex: 2,
              '& td': {
                border: 'none',
                borderBottom: `2px solid ${FP.color.border}`,
                bgcolor: FP.color.surfaceAlt,
              },
            }}
          >
            <TableCell sx={cellHeadSx}>Fecha</TableCell>
            <TableCell sx={cellHeadSx}>Factura</TableCell>
            <TableCell sx={cellHeadSx}>Tipo</TableCell>
            <TableCell sx={cellHeadSx}>Forma</TableCell>
            <TableCell sx={cellHeadSx}>Banco</TableCell>
            <TableCell sx={{ ...cellHeadSx, textAlign: 'right' }} align="right">
              Monto
            </TableCell>
            <TableCell sx={cellHeadSx}>Usuario</TableCell>
            {canDelete && onEliminar ? (
              <TableCell sx={{ ...cellHeadSx, textAlign: 'center' }} align="center">
                Acciones
              </TableCell>
            ) : null}
          </TableRow>
          {rows.map((p) => (
            <TableRow key={p.id} hover>
              <TableCell sx={{ fontSize: '0.85rem', color: FP.color.textPrimary }}>
                {formatearFecha(p.fechacre)}
              </TableCell>
              <TableCell sx={{ fontSize: '0.85rem', fontWeight: 700, color: FP.color.textPrimary }}>
                {p.nfact}
              </TableCell>
              <TableCell
                sx={{
                  maxWidth: 160,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  fontSize: '0.85rem',
                  color: FP.color.textSecondary,
                }}
                title={p.tipofactura}
              >
                {p.tipofactura || '—'}
              </TableCell>
              <TableCell sx={{ fontSize: '0.85rem', color: FP.color.textSecondary }}>
                {p.forma_pago || '—'}
              </TableCell>
              <TableCell
                sx={{
                  maxWidth: 160,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  fontSize: '0.85rem',
                  color: FP.color.textSecondary,
                }}
                title={p.banco}
              >
                {p.banco || '—'}
              </TableCell>
              <TableCell
                align="right"
                sx={{
                  fontSize: '0.88rem',
                  fontWeight: 700,
                  color: FP.color.textPrimary,
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                ${fmtMoney(p.valorpagado)}
              </TableCell>
              <TableCell sx={{ fontSize: '0.82rem', color: FP.color.textSecondary }}>
                {p.usuario || '—'}
              </TableCell>
              {canDelete && onEliminar ? (
                <TableCell align="center">
                  <Tooltip title="Eliminar este pago (revierte el cobro en la factura)">
                    <span>
                      <IconButton
                        size="small"
                        disabled={eliminandoId === p.id}
                        onClick={() => onEliminar(p)}
                        sx={{
                          color: FP.color.danger,
                          '&:hover': { bgcolor: FP.color.dangerSoft },
                        }}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </TableCell>
              ) : null}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

/**
 * Diálogo reutilizable: consulta `facturabackend_listarpagosclientecxc` y permite
 * eliminar un pago con `facturabackend_eliminarpagocredito`. El padre decide
 * si puede eliminar (`canDelete`) y suministra `usuario` y `password` para
 * autenticar cada llamada en el backend.
 */
export function PagosClienteCreditoDetalleDialog({
  open,
  onClose,
  idEmpresa,
  clienteCodigoInicial = '',
  nombreCliente = '',
  canDelete = false,
  usuario = 'system',
  password = '',
  onPagosChanged,
  showError,
  showSuccess,
}) {
  const [clienteInput, setClienteInput] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorCarga, setErrorCarga] = useState(null);
  const [eliminandoId, setEliminandoId] = useState(null);

  const cargar = useCallback(
    async (codigo) => {
      const c = (codigo || '').trim();
      if (!idEmpresa || !c) {
        setRows([]);
        setErrorCarga(null);
        return;
      }
      setLoading(true);
      setErrorCarga(null);
      try {
        const servidor = process.env.REACT_APP_SERVIDOR || '';
        const response = await fetch(servidor + '/api/facturabackend_listarpagosclientecxc', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id_empresa: idEmpresa,
            usuario: usuario || '',
            password: password || '',
            cliente: c,
          }),
        });
        const data = await response.json().catch(() => ({}));
        if (response.ok) {
          setRows(data.pagos || []);
        } else {
          setRows([]);
          setErrorCarga(data.Error || 'Error al cargar pagos');
        }
      } catch {
        setRows([]);
        setErrorCarga('Error de conexión');
      } finally {
        setLoading(false);
      }
    },
    [idEmpresa, usuario, password]
  );

  useEffect(() => {
    if (!open) return;
    const ini = (clienteCodigoInicial || '').trim();
    setClienteInput(ini);
    cargar(ini);
  }, [open, clienteCodigoInicial, cargar]);

  const handleBuscar = () => {
    cargar(clienteInput);
  };

  const handleEliminar = async (p) => {
    if (!canDelete || !p?.id) return;
    const ok = window.confirm(
      `¿Anular el pago de $${fmtMoney(p.valorpagado)} en la factura ${p.nfact}? Se revertirá el cobro en la factura.`
    );
    if (!ok) return;
    setEliminandoId(p.id);
    try {
      const servidor = process.env.REACT_APP_SERVIDOR || '';
      const response = await fetch(servidor + '/api/facturabackend_eliminarpagocredito', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_empresa: idEmpresa,
          usuario: usuario || '',
          password: password || '',
          id_pago: p.id,
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (response.ok) {
        showSuccess?.(data.mensaje || 'Pago eliminado');
        await cargar(clienteInput.trim() || clienteCodigoInicial);
        onPagosChanged?.();
      } else if (showError) {
        showError(data.Error || 'No se pudo eliminar el pago');
      }
    } catch {
      showError?.('Error de conexión al eliminar');
    } finally {
      setEliminandoId(null);
    }
  };

  const tituloNombre =
    (nombreCliente || '').trim() || (clienteInput || clienteCodigoInicial || '').trim() || 'Cliente';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      scroll="paper"
      PaperProps={{
        sx: {
          borderRadius: 2.5,
          border: `1px solid ${FP.color.border}`,
          boxShadow: FP.shadow.md,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pr: 1,
          borderBottom: `1px solid ${FP.color.border}`,
          bgcolor: '#FAFBFE',
        }}
      >
        <Box>
          <Typography
            component="span"
            sx={{
              fontWeight: 800,
              fontSize: '1.05rem',
              color: FP.color.textPrimary,
              letterSpacing: '-0.01em',
            }}
          >
            Pagos registrados
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: FP.color.textSecondary, fontSize: '0.84rem', display: 'block' }}
          >
            {tituloNombre}
          </Typography>
        </Box>
        <IconButton onClick={onClose} aria-label="Cerrar" size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ bgcolor: FP.color.surface }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.2, alignItems: 'center', mb: 2 }}>
          <TextField
            size="small"
            label="Código de cliente"
            value={clienteInput}
            onChange={(e) => setClienteInput(e.target.value)}
            sx={{ minWidth: 220, ...fpInputSx }}
            placeholder="Ej. id en maestcta"
            InputLabelProps={{ shrink: true }}
          />
          <Button
            variant="contained"
            startIcon={<SearchIcon />}
            onClick={handleBuscar}
            disabled={loading || !clienteInput.trim()}
            sx={{
              height: 40,
              bgcolor: FP.color.navy,
              color: '#FFFFFF',
              borderRadius: 1.4,
              textTransform: 'none',
              fontWeight: 700,
              boxShadow: 'none',
              '&:hover': { bgcolor: FP.color.navyDeep, boxShadow: 'none' },
            }}
          >
            Buscar
          </Button>
        </Box>
        {!canDelete && (
          <Alert
            severity="info"
            sx={{
              mb: 2,
              borderRadius: 2,
              border: `1px solid ${FP.color.border}`,
              bgcolor: FP.color.infoSoft,
            }}
          >
            Solo lectura: no tiene permiso para eliminar pagos.
          </Alert>
        )}
        <PagosClienteCreditoDetalleTable
          rows={rows}
          loading={loading}
          onEliminar={handleEliminar}
          eliminandoId={eliminandoId}
          canDelete={canDelete}
          errorMessage={errorCarga}
          emptyMessage={
            (clienteInput || '').trim()
              ? 'No hay cobros CXC en pagoscli para este código.'
              : 'Indique el código de cliente y pulse Buscar.'
          }
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, bgcolor: '#FAFBFE', borderTop: `1px solid ${FP.color.border}` }}>
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            textTransform: 'none',
            borderRadius: 1.4,
            fontWeight: 700,
            borderColor: FP.color.border,
            color: FP.color.textPrimary,
            '&:hover': { bgcolor: FP.color.surfaceAlt, borderColor: FP.color.borderStrong },
          }}
        >
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * Página de Detalle de pagos de clientes a crédito. Aplica los mismos
 * permisos que `RegistroPagosClientesCredito`:
 *   - `tienePermisosModulo` para lectura
 *   - flag `borrar` (puedeAccionFacturacion) para eliminar pagos
 * Usa la clave de programa `pagosclientescreditodetalledialog` (la misma
 * definida en `routesfac.js`).
 */
export function PagosClienteCreditoDetallePage() {
  const { controller, selectedCompany, showSuccess, showError } = useProductosMaterialContext();
  const { nombreNegocio, usuarioLog, claveLog, roleDetalles } = controller;

  const servidor = process.env.REACT_APP_SERVIDOR || '';

  const puedeLeer = useMemo(
    () => tienePermisosModulo(roleDetalles, PROGRAMA_PAGOS_DETALLE),
    [roleDetalles]
  );
  const puedeModificar = useMemo(
    () => puedeAccionFacturacion(roleDetalles, PROGRAMA_PAGOS_DETALLE, 'borrar'),
    [roleDetalles]
  );

  const [clienteInput, setClienteInput] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorCarga, setErrorCarga] = useState(null);
  const [eliminandoId, setEliminandoId] = useState(null);

  const cargar = useCallback(
    async (codigo) => {
      const c = (codigo || '').trim();
      if (!puedeLeer || !selectedCompany?.db_empresa || !c) {
        setRows([]);
        setErrorCarga(null);
        return;
      }
      setLoading(true);
      setErrorCarga(null);
      try {
        const response = await fetch(servidor + '/api/facturabackend_listarpagosclientecxc', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id_empresa: selectedCompany,
            usuario: usuarioLog || '',
            password: claveLog || '',
            cliente: c,
          }),
        });
        const data = await response.json().catch(() => ({}));
        if (response.ok) {
          setRows(data.pagos || []);
        } else {
          setRows([]);
          setErrorCarga(data.Error || 'Error al cargar pagos');
        }
      } catch {
        setRows([]);
        setErrorCarga('Error de conexión');
      } finally {
        setLoading(false);
      }
    },
    [puedeLeer, selectedCompany, usuarioLog, claveLog, servidor]
  );

  const handleBuscar = () => {
    cargar(clienteInput);
  };

  const handleEliminar = async (p) => {
    if (!puedeModificar) {
      showError?.('No tiene permiso para eliminar pagos');
      return;
    }
    if (!p?.id) return;
    const ok = window.confirm(
      `¿Anular el pago de $${fmtMoney(p.valorpagado)} en la factura ${p.nfact}? Se revertirá el cobro en la factura.`
    );
    if (!ok) return;
    setEliminandoId(p.id);
    try {
      const response = await fetch(servidor + '/api/facturabackend_eliminarpagocredito', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_empresa: selectedCompany,
          usuario: usuarioLog || '',
          password: claveLog || '',
          id_pago: p.id,
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (response.ok) {
        showSuccess?.(data.mensaje || 'Pago eliminado');
        await cargar(clienteInput);
      } else {
        showError?.(data.Error || 'No se pudo eliminar el pago');
      }
    } catch {
      showError?.('Error de conexión al eliminar');
    } finally {
      setEliminandoId(null);
    }
  };

  if (!puedeLeer) {
    return (
      <Box component="main" sx={{ minHeight: '100vh', bgcolor: FP.color.surface }}>
        <PageHeader
          title="Detalle de pagos — clientes a crédito"
          subtitle={
            nombreNegocio
              ? `${nombreNegocio} · Pagos por cliente (pagoscli)`
              : 'Pagos por cliente (pagoscli)'
          }
        />
        <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 }, display: 'flex', justifyContent: 'center' }}>
          <Card
            elevation={0}
            sx={{
              ...cardShellSx,
              maxWidth: 560,
              width: '100%',
              textAlign: 'center',
              p: { xs: 3, md: 4 },
            }}
          >
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: 1.6,
                mx: 'auto',
                mb: 2.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: FP.color.dangerSoft,
                color: FP.color.danger,
              }}
            >
              <LockOutlinedIcon sx={{ fontSize: 30 }} />
            </Box>
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: '1.25rem',
                color: FP.color.textPrimary,
                mb: 1,
                letterSpacing: '-0.01em',
              }}
            >
              Acceso denegado
            </Typography>
            <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.95rem', mb: 1.2 }}>
              No tiene permiso del programa «{PROGRAMA_PAGOS_DETALLE}» en roles para consultar el detalle
              de pagos de clientes a crédito.
            </Typography>
            <Typography sx={{ color: FP.color.textMuted, fontSize: '0.85rem' }}>
              Por favor, contacte al administrador del sistema para solicitar los permisos necesarios.
            </Typography>
          </Card>
        </Box>
      </Box>
    );
  }

  return (
    <Box component="main" sx={{ minHeight: '100vh', bgcolor: FP.color.surface }}>
      <PageHeader
        title="Detalle de pagos — clientes a crédito"
        subtitle={
          nombreNegocio
            ? `${nombreNegocio} · Pagos por cliente (pagoscli)`
            : 'Pagos por cliente (pagoscli)'
        }
      />

      <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
        <Alert
          severity="info"
          sx={{
            mb: 2,
            borderRadius: 2,
            border: `1px solid ${FP.color.border}`,
            bgcolor: FP.color.infoSoft,
          }}
        >
          Consulta el detalle de cobros registrados en `pagoscli` para un cliente. Indique el código del
          cliente (id en `maestcta`) y pulse Buscar.
        </Alert>

        {!puedeModificar && (
          <Alert
            severity="warning"
            sx={{
              mb: 2,
              borderRadius: 2,
              border: `1px solid ${FP.color.border}`,
              bgcolor: FP.color.warningSoft,
            }}
          >
            Solo lectura: no tiene permiso para eliminar pagos.
          </Alert>
        )}

        <Card elevation={0} sx={cardShellSx}>
          <Box
            sx={{
              px: { xs: 2, md: 3 },
              py: 1.6,
              borderBottom: `1px solid ${FP.color.border}`,
              bgcolor: '#FAFBFE',
              display: 'flex',
              alignItems: 'center',
              gap: 1.2,
              flexWrap: 'wrap',
            }}
          >
            <TextField
              size="small"
              label="Código de cliente"
              value={clienteInput}
              onChange={(e) => setClienteInput(e.target.value)}
              sx={{ minWidth: 260, ...fpInputSx }}
              placeholder="Ej. id en maestcta"
              InputLabelProps={{ shrink: true }}
            />
            <Button
              variant="contained"
              startIcon={<SearchIcon />}
              onClick={handleBuscar}
              disabled={loading || !clienteInput.trim()}
              sx={{
                height: 40,
                bgcolor: FP.color.navy,
                color: '#FFFFFF',
                borderRadius: 1.4,
                textTransform: 'none',
                fontWeight: 700,
                boxShadow: 'none',
                '&:hover': { bgcolor: FP.color.navyDeep, boxShadow: 'none' },
              }}
            >
              Buscar
            </Button>
          </Box>
          <CardContent sx={{ p: { xs: 2, md: 3 } }}>
            <PagosClienteCreditoDetalleTable
              rows={rows}
              loading={loading}
              onEliminar={handleEliminar}
              eliminandoId={eliminandoId}
              canDelete={puedeModificar}
              errorMessage={errorCarga}
              emptyMessage={
                (clienteInput || '').trim()
                  ? 'No hay cobros CXC en pagoscli para este código.'
                  : 'Indique el código de cliente y pulse Buscar.'
              }
            />
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
}

export default PagosClienteCreditoDetallePage;
